import clsx from 'clsx';
import { isString } from 'lodash-es';
import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ErrorPlaneSvg } from 'assets/error-plane.svg';
import { Typography, styled } from 'netline-ui';
import { MainErrorItem } from 'types';

import ErrorActions from './ErrorActions';
import ErrorDetails from './ErrorDetails';
import ErrorText from './ErrorText';
import styles, { errorViewClasses, errorViewLogoClasses } from './ErrorView.styles';

type ErrorViewProps = Omit<HTMLAttributes<HTMLDivElement>, keyof MainErrorItem> &
  Omit<MainErrorItem, 'logo'> & { logo?: MainErrorItem['logo'] | FC<SVGProps<SVGElement>> };

const ErrorViewRoot = styled('div', { name: errorViewClasses.$name })(styles);

const renderLogo = (logo: FC<SVGProps<SVGElement>> | string | null | undefined, alt: string) => {
  if (logo === null) {
    return null;
  }

  const props = {
    className: clsx(errorViewClasses.logo, { [errorViewLogoClasses.custom]: !!logo }),
    'data-testid': 'ErrorView-logo',
  };

  const Logo = logo || ErrorPlaneSvg;
  if (isString(Logo)) {
    return <img src={Logo} alt={alt} {...props} />;
  }

  return <Logo {...props} />;
};

const ErrorView = ({ className, logo: logoProp, title, message, actions, details, ...props }: ErrorViewProps) => {
  const { t } = useTranslation();
  const logo = renderLogo(logoProp, t('session_expired.title'));

  return (
    <ErrorViewRoot className={clsx(className, errorViewClasses.root)} data-testid="ErrorView-root" {...props}>
      {logo}
      <div className={errorViewClasses.container}>
        <Typography variant="h2" className={errorViewClasses.title} data-testid="ErrorView-title">
          <ErrorText text={title} />
        </Typography>
        <Typography variant="body2" className={errorViewClasses.message} data-testid="ErrorView-message">
          <ErrorText text={message} />
        </Typography>
        {!!actions?.length && (
          <ErrorActions className={errorViewClasses.actions} data-testid="ErrorView-actions" actions={actions} />
        )}
        {details && (
          <ErrorDetails className={errorViewClasses.details} data-testid="ErrorView-details" details={details} />
        )}
      </div>
    </ErrorViewRoot>
  );
};

export default ErrorView;
